import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Typography,
  Row,
  Col,
  Button,
  Checkbox,
  message,
  Space,
} from "antd";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Sidebar from "./Sidebar";
import { BASE_URL } from "./config";
import HeaderBar from "./HeaderBar";
import Footer from "./Footer";

const { Content, Sider } = Layout;
const { Text } = Typography;

function Slots() {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [user, setUser] = useState(null);

  // Get user
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) setUser(JSON.parse(storedUser));
  }, []);

  const companyId = user?.company_id;

  // Generate time slots
  const generateSlots = () => {
    let slots = [];
    for (let hour = 9; hour < 20; hour++) {
      let h = hour % 12 === 0 ? 12 : hour % 12;
      let ampm = hour < 12 ? "AM" : "PM";
      slots.push(`${h.toString().padStart(2, "0")}:00 ${ampm}`);
      slots.push(`${h.toString().padStart(2, "0")}:30 ${ampm}`);
    }
    return slots;
  };
  const allSlots = generateSlots();

  const formatDate = (date) => {
    return new Date(date).toISOString().split("T")[0];
  };

  const fetchBookedSlots = async (date) => {
    const res = await fetch(
      `${BASE_URL}/slots.php?action=get&date=${formatDate(date)}&companyId=${companyId}`
    );
    const data = await res.json();
    setBookedSlots(data.bookedSlots || []);
  };

  useEffect(() => {
    if (selectedDate) {
      fetchBookedSlots(selectedDate);
      setSelectedSlots([]);
      setSelectAll(false);
    }
  }, [selectedDate]);

  // Toggle single slot
  const handleSelectSlot = (slot) => {
    setSelectedSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );
  };

  // Toggle Select All
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedSlots(allSlots.filter((slot) => !bookedSlots.includes(slot)));
    } else {
      setSelectedSlots([]);
    }
  };

  // Block / Unblock
  const handleAction = async (action) => {
    if (!selectedDate) return message.warning("Please select a date first!");
    if (selectedSlots.length === 0)
      return message.warning("Select at least one slot!");

    const res = await fetch(`${BASE_URL}/slots.php?action=${action}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        date: formatDate(selectedDate),
        slots: selectedSlots.join(","),
        companyId,
      }),
    });

    const data = await res.json();
    if (data.success) {
      message.success(
        action === "block"
          ? "Slots blocked successfully!"
          : "Slots unblocked successfully!"
      );
      fetchBookedSlots(selectedDate);
      setSelectedSlots([]);
      setSelectAll(false);
    } else {
      message.error(data.message || "Action failed.");
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", fontFamily: "Poppins" }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        collapsedWidth="80"
      >
        <Sidebar collapsed={collapsed} user={user} />
      </Sider>

      <Layout>
        <HeaderBar title="Select Slots" />

        <Content style={{ margin: "16px", paddingBottom: 40 }}>
          {/* Top actions */}
          <Row
            justify="space-between"
            align="middle"
            gutter={[12, 12]}
            style={{ marginBottom: 20 }}
          >
            <Col xs={24} md={12}>
              {selectedDate && (
                <Text strong>
                  Selected Date: {selectedDate.toDateString()}
                </Text>
              )}
            </Col>

            <Col xs={24} md="auto">
              <Space wrap style={{ justifyContent: "flex-end", width: "100%" }}>
                <Checkbox
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  Select All
                </Checkbox>

                <Button type="primary" danger onClick={() => handleAction("block")}>
                  Block
                </Button>

                <Button onClick={() => handleAction("unblock")}>Unblock</Button>
              </Space>
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            {/* Calendar Section */}
            <Col xs={24} md={10} lg={8}>
              <Card bodyStyle={{ padding: 12 }}>
                <Calendar
                  onChange={setSelectedDate}
                  value={selectedDate}
                  minDate={new Date()}
                  className="w-full"
                />
              </Card>
            </Col>

            {/* Slots Section */}
            <Col xs={24} md={14} lg={16}>
              {selectedDate && (
                <>
                  <Text strong style={{ display: "block", marginBottom: 12 }}>
                    Slots for: {selectedDate.toDateString()}
                  </Text>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {allSlots.map((slot) => {
                      const isBooked = bookedSlots.includes(slot);
                      const isSelected = selectedSlots.includes(slot);

                      return (
                        <Card
                          key={slot}
                          size="small"
                          hoverable={!isBooked}
                          style={{
                            background: isBooked
                              ? "#f2f2f2"
                              : isSelected
                              ? "#1677ff"
                              : "#fff",
                            color: isBooked
                              ? "#999"
                              : isSelected
                              ? "#fff"
                              : "#000",
                            border: isSelected
                              ? "2px solid #0958d9"
                              : "1px solid #ddd",
                            minHeight: 60,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 8,
                            fontSize: 14,
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={false}
                            onChange={() => handleSelectSlot(slot)}
                          >
                            {slot}
                          </Checkbox>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Content>

        <Footer user={user} />
      </Layout>
    </Layout>
  );
}

export default Slots;
